import * as React from "react";
import TopBar from "./TopBar";
import ConversationView from "./ConversationView";
import Composer from "./Composer";
import QuickActions from "./QuickActions";
import ChatHistorySidebar from "./ChatHistorySidebar";
import ChangeProposalSidebar from "./ChangeProposalSidebar";
import { ConversationMessage, MessageFile, ModelInfo, QuickAction, UserProfile, ModelChoice, UserSettings, AttachmentRef, FulltextFileRef, OwuiFolder, ChangeProposal, ChangeProposalState, ApplyMode } from "@lupp/shared";

/** At/above this pane width the history sidebar docks (pushes content) instead of overlaying. */
const DOCK_MIN_WIDTH = 600;

export interface AppShellProps {
  draft: string;
  isSending: boolean;
  messages: ConversationMessage[];
  quickActions: QuickAction[];
  model: ModelInfo;
  selectedModelId: string;
  models: ModelChoice[];
  user?: UserProfile | null;
  userSettings?: UserSettings | null;
  onModelSelect: (model: ModelChoice) => void;
  onDraftChange: (draft: string) => void;
  onQuickAction: (action: QuickAction) => void;
  onSend: (files: MessageFile[], attachments: AttachmentRef[], fulltextFiles: FulltextFileRef[]) => void;
  onSendText: (text: string) => void;
  onStop: () => void;
  onNewChat: () => void;
  webSearch: boolean;
  onToggleWebSearch: () => void;
  onSwitchBranch: (messageId: string, dir: -1 | 1) => void;
  onEditSave: (messageId: string, text: string) => void;
  onEditResend: (messageId: string, text: string) => void;
  onDeleteMessage: (messageId: string) => void;
  scrollTarget?: { id: string | null; seq: number };
  historyOpen: boolean;
  onToggleHistory: () => void;
  onDockedChange: (docked: boolean) => void;
  // Änderungsvorschläge-Sidebar (gegenseitig exklusiv zur History)
  proposalsOpen: boolean;
  proposals: ChangeProposal[];
  proposalStates: Record<string, ChangeProposalState>;
  applyMode: ApplyMode;
  onApplyModeChange: (mode: ApplyMode) => void;
  onAcceptProposal: (id: string) => void;
  onRejectProposal: (id: string) => void;
  onAcceptAllProposals: () => void;
  onRejectAllProposals: () => void;
  onScrollToParagraph: (paragraphId: string) => void;
  onCloseProposals: () => void;
  onOpenProposals: (messageId: string) => void;
  chatId?: string;
  onSelectChat: (chatId: string, folderId?: string | null) => void;
  folders: OwuiFolder[];
  activeFolderId: string | null;
  onEnterFolder: (folderId: string) => void;
  onClearProject: () => void;
  onProjectCreated: () => void;
  historyRefreshKey: number;
  chatTitle: string;
  onMoveChat: (chatId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, parentId: string | null) => void;
}

interface AppShellState {
  docked: boolean; // pane wide enough to dock the history sidebar beside the content
}

export default class AppShell extends React.Component<AppShellProps, AppShellState> {
  state: AppShellState = { docked: false };
  private rootRef = React.createRef<HTMLDivElement>();
  private resizeObserver?: ResizeObserver;

  componentDidMount(): void {
    const el = this.rootRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? el.clientWidth;
        const docked = w >= DOCK_MIN_WIDTH;
        if (docked !== this.state.docked) {
          this.setState({ docked });
          this.props.onDockedChange(docked); // App braucht es fürs Auto-Aufklappen der Vorschläge
        }
      });
      this.resizeObserver.observe(el);
      // Initialer Dock-Status (synchron beim Mount), damit das Auto-Aufklappen sofort korrekt ist.
      const w0 = el.clientWidth;
      const docked0 = w0 >= DOCK_MIN_WIDTH;
      if (docked0 !== this.state.docked) this.setState({ docked: docked0 });
      this.props.onDockedChange(docked0);
    }
  }

  componentWillUnmount(): void {
    this.resizeObserver?.disconnect();
  }

  render(): JSX.Element {
    const {
      draft,
      isSending,
      messages,
      quickActions,
      model,
      selectedModelId,
      models,
      user,
      userSettings,
      onModelSelect,
      onDraftChange,
      onQuickAction,
      onSend,
      onSendText,
      onStop,
      onNewChat,
      webSearch,
      onToggleWebSearch,
      onSwitchBranch,
      onEditSave,
      onEditResend,
      onDeleteMessage,
      scrollTarget,
      historyOpen,
      onToggleHistory,
      chatId,
      onSelectChat,
      folders,
      activeFolderId,
      onEnterFolder,
      onClearProject,
      onProjectCreated,
      historyRefreshKey,
      chatTitle,
      onMoveChat,
      onMoveFolder,
      proposalsOpen,
      proposals,
      proposalStates,
      applyMode,
      onApplyModeChange,
      onAcceptProposal,
      onRejectProposal,
      onAcceptAllProposals,
      onRejectAllProposals,
      onScrollToParagraph,
      onCloseProposals,
      onOpenProposals,
    } = this.props;

    const hasMessages = messages.length > 0;
    const { docked } = this.state;
    const anySidebarOpen = historyOpen || proposalsOpen;
    const dockedOpen = anySidebarOpen && docked;

    // An overlay sidebar closes on any topbar action; a docked one stays open.
    const closeOverlay = (): void => {
      if (historyOpen && !docked) onToggleHistory();
      if (proposalsOpen && !docked) onCloseProposals();
    };

    return (
      <div className={`taskpane-app${dockedOpen ? " taskpane-app--history-docked" : ""}`} ref={this.rootRef}>
        <TopBar
          selectedModelId={selectedModelId}
          models={models}
          onModelSelect={(m): void => {
            onModelSelect(m);
            closeOverlay();
          }}
          onNewChat={(): void => {
            onNewChat();
            closeOverlay();
          }}
          onToggleHistory={onToggleHistory}
          historyOpen={historyOpen}
          user={user}
          userSettings={userSettings}
          folders={folders}
          activeFolderId={activeFolderId}
          onClearProject={onClearProject}
          chatTitle={chatTitle}
        />

        <div className="taskpane-body">
          <div className="taskpane-main">
            {hasMessages ? (
              <ConversationView messages={messages} isSending={isSending} onSend={onSendText} onSwitchBranch={onSwitchBranch} onEditSave={onEditSave} onEditResend={onEditResend} onDeleteMessage={onDeleteMessage} onOpenProposals={onOpenProposals} scrollTarget={scrollTarget} />
            ) : (
              <QuickActions actions={quickActions} onSelect={onQuickAction} />
            )}

            <Composer
              draft={draft}
              isSending={isSending}
              isEmpty={!hasMessages}
              model={model}
              onDraftChange={onDraftChange}
              onSend={onSend}
              onStop={onStop}
              webSearch={webSearch}
              onToggleWebSearch={onToggleWebSearch}
            />
          </div>

          {anySidebarOpen && !docked && (
            <div className="chat-history__backdrop" onClick={historyOpen ? onToggleHistory : onCloseProposals} aria-hidden="true" />
          )}
          {historyOpen && (
            <ChatHistorySidebar
              docked={docked}
              currentChatId={chatId}
              onSelectChat={onSelectChat}
              onClose={onToggleHistory}
              folders={folders}
              activeFolderId={activeFolderId}
              onEnterFolder={onEnterFolder}
              onProjectCreated={onProjectCreated}
              refreshKey={historyRefreshKey}
              onMoveChat={onMoveChat}
              onMoveFolder={onMoveFolder}
            />
          )}
          {proposalsOpen && (
            <ChangeProposalSidebar
              docked={docked}
              proposals={proposals}
              states={proposalStates}
              applyMode={applyMode}
              onApplyModeChange={onApplyModeChange}
              onAccept={onAcceptProposal}
              onReject={onRejectProposal}
              onAcceptAll={onAcceptAllProposals}
              onRejectAll={onRejectAllProposals}
              onScrollTo={onScrollToParagraph}
              onClose={onCloseProposals}
            />
          )}
        </div>
      </div>
    );
  }
}
